/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { DeviceModel } from '../../device';

export class InternalRatingsUtility {
    private static requiredFields = {
        ratingType: ['adultLevel', 'blockUnratedPrograms', 'displayRank'],
        ratingValue: ['description', 'level'],
    };

    private static msoNames = {
        claro: 'claro',
        clarodev: 'clarodev',
        llacl: 'llacl',
        llacldev: 'llacldev',
        llacr: 'llacr',
        llacrdev: 'llacrdev',
        llapa: 'llapa',
        llapadev: 'llapadev',
    };

    private static enUsMissingRatings = {
        ratingType: {
            'tivo:rt.1': {
                adultLevel: '50',
                blockUnratedPrograms: 'true',
                displayRank: '2',
            },

            'tivo:rt.2': {
                adultLevel: '65',
                blockUnratedPrograms: 'true',
                displayRank: '1',
            },
        },

        ratingValue: {
            G: {
                description:
                    'G: General Audiences. Most parents would find this material suitable for all ages.',
                level: '10',
            },
            PG: {
                description:
                    'PG: Parental Guidance Suggested. Parents may find portions of this material unsuitable for younger children.',
                level: '20',
            },
            'PG-13': {
                description:
                    'PG-13: Parents Strongly Cautioned. This material contains portions that many parents would find unsuitable for children under 13 years of age.',
                level: '30',
            },
            R: {
                description:
                    'R: Restricted. This material is specifically designed to be viewed by adults and therefore may be unsuitable for children under 17.',
                level: '40',
            },
            'NC-17': {
                description:
                    'NC-17: Adult Content. This material contains intense, disturbing, or graphically-portrayed themes or content that are not suitable for viewing by children under 17.',
                level: '50',
            },
            AO: {
                description:
                    'AO: Explicit Sexual Content. This material contains explicit sexual content and is intended to be viewed by adults ONLY.',
                level: '60',
            },
            NR: {
                description: 'NR',
                level: '70',
            },
            'TV-Y': {
                description:
                    'TV-Y: All children. These shows are designated to be appropriate for all children.',
                level: '10',
            },
            'TV-Y7': {
                description:
                    'TV-Y7: Directed to older children. These programs are designed for children age 7 and above.',
                level: '20',
            },
            'TV-G': {
                description:
                    'TV-G: General Audiences. Most parents would find these programs suitable for all ages.',
                level: '30',
            },
            'TV-PG': {
                description:
                    'TV-PG: Parental Guidance Suggested. These programs contain material that parents may find unsuitable for younger children.',
                level: '40',
            },
            'TV-14': {
                description:
                    'TV-14: Parents Strongly Cautioned. These shows contain material that many parents would find unsuitable for children under 14.',
                level: '50',
            },
            'TV-MA': {
                description:
                    'TV-MA: Mature Audience Only. These shows are designed to be viewed by adults and therefore may be unsuitable for children under 17.',
                level: '60',
            },
            'TV-NR': {
                description: 'TV-NR',
                level: '70',
            },
        },
    };

    private static esUsMissingRatings = {
        ratingType: {
            'tivo:rt.1': {
                adultLevel: '50',
                blockUnratedPrograms: 'true',
                displayRank: '2',
            },

            'tivo:rt.2': {
                adultLevel: '65',
                blockUnratedPrograms: 'true',
                displayRank: '1',
            },
        },

        ratingValue: {
            G: {
                description:
                    'G: Todo tipo de público. La mayoría de los padres consideran que este contenido es apto para todas las edades.',
                level: '10',
            },
            PG: {
                description:
                    'PG: Se sugiere la orientación de un adulto. Los padres pueden encontrar partes de este contenido inapropiadas para los niños más pequeños.',
                level: '20',
            },
            'PG-13': {
                description:
                    'PG- 13: Los padres fuertemente advertidos. Este material contiene porciones que muchos padres encontrarían inadecuadas para nifos menores de 13 años de edad.',
                level: '30',
            },
            R: {
                description:
                    'R: Restringido. Este contenido se diseño especialemente para ser visto por adultos, y por lo tanto, puede ser inadecuado para niños menores de 17 años.',
                level: '40',
            },
            'NC-17': {
                description:
                    'Todos los programas pueden reproducirse sin la necesidad de ingresar un PIN. Esto incluye las películas con calificación:\nAO: Contenido sexual explicito. Este material muestra contenido sexual explicito, y está destinado para ser visto solo por adultos.',
                level: '50',
            },
            AO: {
                description:
                    'AO: Contenido sexual explícito. Este material muestra contenido sexual explícito, y está destinado para ser visto solo por adultos.',
                level: '60',
            },
            NR: {
                description: 'NR',
                level: '70',
            },
            'TV-Y': {
                description:
                    'TV-Y: Todos los niños. Estos programas están apropiadamente diseñados para niños.',
                level: '10',
            },
            'TV-Y7': {
                description:
                    'TV-Y7: Dirigido a niños más grandes Estos programas están diseñados para niños mayores de 7 años.',
                level: '20',
            },
            'TV-G': {
                description:
                    'TV-G: Todo tipo de público. La mayoría de los padres consideran que este contenido es apto para todas las edades.',
                level: '30',
            },
            'TV-PG': {
                description:
                    'TV-PG: Se sugiere la orientación de un adulto. Los padres pueden encontrar partes de este contenido inapropiadas para los niños más pequeñios.',
                level: '40',
            },
            'TV-14': {
                description:
                    'TV-14: Advertencia importante para los padres. El contenido de estos programas es considerado por muchos padres como inadecuado para niños menores de 14 años.',
                level: '50',
            },
            'TV-MA': {
                description:
                    'Todos los programas pueden reproducirse sin la necesidad de ingresar un PIN. Esto incluye las películas con calificación:\nTV-MA: Audiencia Madura Solamente. Estos programas están diseñados para ser vistos por adultos, y por lo tanto podrían ser inadecuados para niños menores de 17 años.',
                level: '60',
            },
            'TV-NR': {
                description: 'TV-NR',
                level: '70',
            },
        },
    };

    private static enLatamMissingRatings = {
        ratingType: {
            'tivo:rt.7': {
                adultLevel: '35',
                blockUnratedPrograms: 'true',
                displayRank: '1',
            },
        },
        ratingValue: {
            General: {
                description: 'For all age groups, for the entire family.',
                level: '10',
            },
            '+12': {
                description:
                    'Intended for a general audience, may not be suitable for younger children (under the age of 12).',
                level: '20',
            },
            '+18': {
                description: 'Intended for viewers 18 years and older.',
                level: '30',
            },
            Adult: {
                description:
                    'Intended for viewers 18 years and older. Contains scenes of real and explicit sexual activity.',
                level: '40',
            },
        },
    };

    private static esLatamMissingRatings = {
        ratingType: {
            'tivo:rt.7': {
                adultLevel: '35',
                blockUnratedPrograms: 'true',
                displayRank: '1',
            },
        },
        ratingValue: {
            General: {
                description: 'Para todas las edades, familiar.',
                level: '10',
            },
            '+12': {
                description:
                    'Para todas las edades, familiar, puede no ser apta para menores de 12 años.',
                level: '20',
            },
            '+18': {
                description: 'Para mayores de 18 años.',
                level: '30',
            },
            Adulto: {
                description: 'Para adultos mayores de 18 años. Contiene escenas de sexo.',
                level: '40',
            },
        },
    };

    public static getMissingRatingFields() {
        const msoName = DeviceModel.getTiVoSettings().getMsoName();
        const lang = DeviceModel.getTVProperties().getLang();

        switch (msoName) {
            case this.msoNames.claro:
            case this.msoNames.clarodev:
            case this.msoNames.llacl:
            case this.msoNames.llacldev:
                return lang === 'en'
                    ? InternalRatingsUtility.enLatamMissingRatings
                    : InternalRatingsUtility.esLatamMissingRatings;

            default:
                return lang === 'en'
                    ? InternalRatingsUtility.enUsMissingRatings
                    : InternalRatingsUtility.esUsMissingRatings;
        }
    }

    public static getRequiredFields() {
        return InternalRatingsUtility.requiredFields;
    }
}
